import React, { ReactNode, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { CloseIcon } from '../icons'

const ESC_KEY = 'Escape'

const noOp = () => {}

type Props = {
  onClose?: (event: KeyboardEvent | React.SyntheticEvent) => void
  shouldCloseOnOverlayClick?: boolean
  overlayStyle?: object
  overlayClassName?: string
  contentClassName?: string
  modal?: boolean
  ariaLabel?: string
  closeButtonLabel?: string
  children: ReactNode
}

export function Dialog({
  onClose,
  overlayStyle,
  children,
  shouldCloseOnOverlayClick = true,
  overlayClassName = '',
  contentClassName = '',
  modal = false,
  ariaLabel,
  closeButtonLabel = 'Close dialog',
}: Props) {
  const content = useRef<HTMLDivElement>(null)
  const mountNode = document.getElementById('root')

  useEffect(() => {
    const dialog = content.current
    if (!modal || !mountNode || !dialog) return

    const previousInert = mountNode.getAttribute('inert')
    mountNode.setAttribute('inert', '')
    if (!dialog.contains(document.activeElement)) dialog.focus()

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], area[href], button, input, select, textarea, iframe, [tabindex], [contenteditable="true"]',
        ),
      ).filter((element) => {
        if (element.tabIndex < 0 || element.matches(':disabled') || element.closest('[hidden], [inert]')) return false
        for (let node: HTMLElement | null = element; node; node = node.parentElement) {
          const style = window.getComputedStyle(node)
          if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false
        }
        return true
      })
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (!first || !focusable.includes(active as HTMLElement) || active === (event.shiftKey ? first : last)) {
        event.preventDefault()
        const target = (event.shiftKey ? last : first) ?? dialog
        target.focus()
      }
    }

    document.addEventListener('keydown', handleTab)
    return () => {
      document.removeEventListener('keydown', handleTab)
      if (previousInert === null) mountNode.removeAttribute('inert')
      else mountNode.setAttribute('inert', previousInert)
    }
  }, [modal, mountNode])

  useEffect(() => {
    const _handleEscapeKey = (event: KeyboardEvent) => {
      if (onClose && event.key === ESC_KEY) {
        event.stopPropagation()
        onClose(event)
      }
    }

    window.addEventListener('keydown', _handleEscapeKey)
    return () => {
      window.removeEventListener('keydown', _handleEscapeKey)
    }
  }, [onClose])

  if (!mountNode) return null

  const overlayClassNames =
    'p-lg inset-0 fixed flex items-center justify-center bg-black-transparent z-50 ' + (overlayClassName ?? '')

  const contentClassNames = 'relative max-h-window ' + (contentClassName ?? '')

  return ReactDOM.createPortal(
    <div onClick={shouldCloseOnOverlayClick ? onClose : noOp} className={overlayClassNames} style={overlayStyle}>
      <div
        ref={content}
        role={modal ? 'dialog' : undefined}
        aria-modal={modal ? true : undefined}
        aria-label={modal ? ariaLabel : undefined}
        tabIndex={modal ? -1 : undefined}
        className={contentClassNames}
        onClick={(evt) => evt.stopPropagation()}
      >
        {onClose && (
          <button className={'btn btn-icon absolute right-sm top-sm'} onClick={onClose} aria-label={closeButtonLabel}>
            <CloseIcon />
          </button>
        )}
        {children}
      </div>
    </div>,
    modal ? document.body : mountNode,
  )
}
